import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import Sidenav from 'examples/Sidenav';

import theme from 'assets/theme';
import themeRTL from 'assets/theme/theme-rtl';
import themeDark from 'assets/theme-dark';
import themeDarkRTL from 'assets/theme-dark/theme-rtl';

import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';

import routes from 'routes';
import { useMaterialUIController, setMiniSidenav } from 'context';

import brandWhite from 'assets/images/logo-ct.png';
import brandDark from 'assets/images/logo-ct-dark.png';

import { AppRouter } from './app/routes/AppRouter';
import { AppStateProvider } from 'shared/appState/AppStateProvider';
import { logout } from 'core/auth/logout';

import GlobalQuickActions from 'shared/ui/QuickActions/GlobalQuickActions';
import { QuickActionsBridgeProvider } from 'shared/ui/QuickActions/QuickActionsBridge';
import { useMe } from 'core/me/provider/MeProvider';

const solisThemeDark = createTheme(themeDark, {
  palette: {
    background: {
      default: '#0B1220',
      paper: '#0F1B33',
    },
  },
});

// ✅ tokenStorage key'in senin localStorage'da: authTokens
const AUTH_TOKENS_KEY = 'authTokens';

/** ✅ 20 sektör için: tek yerden yönet */
const SECTOR_REGISTRY = [
  { sector: 'PILATES', match: (p) => p.startsWith('/app/pilates') || p.startsWith('/pilates') },
  // { sector: "PHYSIO", match: (p) => p.startsWith("/app/physio") || p.startsWith("/physio") },
];

function resolveSector(pathname) {
  const hit = SECTOR_REGISTRY.find((x) => x.match(pathname));
  return hit?.sector ?? null;
}

/** ✅ Testte: her şey açık */

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  /** ✅ Setup / Auth ekranlarında chrome (Sidenav) görünmesin */
  const hideChrome =
    pathname.startsWith('/rsvp/') || // ✅ bunu ekle
    pathname.startsWith('/app/setup') ||
    pathname.startsWith('/verify-email') ||
    pathname.startsWith('/sign-in') ||
    pathname.startsWith('/sign-up') ||
    pathname === '/login' ||
    pathname === '/register';
  const { me } = useMe();

  /** ✅ Sidenav meta */
  /** ✅ Sidenav meta (gerçek me’den) */
  const sidenavMeta = useMemo(() => {
    // sector: backend varsa onu kullan, yoksa url’den resolve et
    const sector = me?.sector?.sectorKey ?? me?.companyType ?? resolveSector(pathname) ?? null;

    const plan = me?.plan ?? 'TRIAL';

    // permissions: backend'ten gelen gerçek izinler
    const perms = me?.sector?.permissions ?? {};

    return { sector, plan, perms };
  }, [me, pathname]);

  /** ✅ rtlCache set */
  useEffect(() => {
    const cacheRtl = createCache({ key: 'rtl', stylisPlugins: [rtlPlugin] });
    setRtlCache(cacheRtl);
  }, []);

  /** ✅ Multi-tab logout sync */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== AUTH_TOKENS_KEY) return;

      if (!e.newValue) {
        logout({ redirectTo: '/sign-in' });
        return;
      }

      try {
        const parsed = JSON.parse(e.newValue);
        if (!parsed?.accessToken) logout({ redirectTo: '/sign-in' });
      } catch {
        logout({ redirectTo: '/sign-in' });
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const shell = (
    <>
      <CssBaseline />

      <QuickActionsBridgeProvider>
        {/* ✅ Dashboard chrome sadece hideChrome değilken */}
        {layout === 'dashboard' && !hideChrome && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Solis UP"
              routes={routes}
              meta={sidenavMeta}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />

            {/* ✅ Quick Actions */}
            <GlobalQuickActions />
          </>
        )}

        <AppRouter />
      </QuickActionsBridgeProvider>
    </>
  );

  if (direction === 'rtl') {
    if (!rtlCache) return null;

    return (
      <CacheProvider value={rtlCache}>
        <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>{shell}</ThemeProvider>
      </CacheProvider>
    );
  }

  return <ThemeProvider theme={darkMode ? solisThemeDark : theme}>{shell}</ThemeProvider>;
}
